import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { programs, sites } from '@cwos/api-client';
import type { sites as SitesNS } from '@cwos/api-client';
import { ConfirmAction, Pill, SiteMapPicker, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { useNav } from '../navigation.js';
import { SettingsActions, SettingsCard, SettingsField, SettingsHeader } from './common.js';
import { canEdit, statusBadge } from './organizationLogic.js';
import { LoadFailure } from '../LoadFailure.js';

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;

/**
 * A site as an operational object: where it is, who works out of it, whether
 * clock-ins are checked against it, and what retiring it would cost.
 *
 * The geofence control on this screen writes the same `work_geofence` row the
 * punch evaluator reads. There is deliberately no setting here that has no
 * operational effect — a radius shown on this page is a radius a clock-in is
 * measured against.
 */
export function SiteDetail() {
  const { siteId } = useParams({ strict: false }) as { siteId: string };
  const qc = useQueryClient();
  const { has } = usePermissions();
  const navigate = useNav();
  const canManage = has('location:manage');

  const detail = useQuery({
    queryKey: ['sites', 'detail', siteId],
    queryFn: () => sites.detail(siteId),
  });
  const site = detail.data;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['sites'] });
    void qc.invalidateQueries({ queryKey: ['sites', 'detail', siteId] });
    // The Program screen edits the same relationship rows from the other side.
    void qc.invalidateQueries({ queryKey: ['programs'] });
  };

  if (detail.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 1080 }}>
        <SkeletonSet surface="table" rows={6} />
      </div>
    );
  }
  if (detail.isError || !site) {
    return (
      <div className="rn-col" style={{ maxWidth: 680 }}>
        <LoadFailure
          error={detail.error}
          mainRead
          message="We couldn’t load that site. It may have been removed, or it may belong to a different organization."
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const badge = statusBadge(site.status);
  const editable = canManage && canEdit(site.status);

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <div className="org-detail-head">
        <button type="button" className="rn-btn-quiet" onClick={() => navigate('/settings/sites')}>
          ← All sites
        </button>
        <SettingsHeader title={site.name} blurb={site.addressSummary ?? undefined} />
        <div className="org-detail-meta">
          <Pill variant={badge.variant} dot>
            {badge.label}
          </Pill>
          <span className="rn-muted">{badge.meaning}</span>
          {site.code ? <span className="rn-muted">Code: {site.code}</span> : null}
        </div>
      </div>

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">At a glance</h2>
          <span className="rn-muted">Last 30 days</span>
        </div>
        <div className="org-stats">
          <div className="org-stat">
            <span className="org-stat-value">{site.summary.programs}</span>
            <span className="org-stat-label">Programs</span>
          </div>
          <div className="org-stat">
            <span className="org-stat-value">{site.summary.workers}</span>
            <span className="org-stat-label">People</span>
          </div>
          <div className="org-stat">
            <span className="org-stat-value">{site.summary.scheduledShifts}</span>
            <span className="org-stat-label">Shifts scheduled here</span>
          </div>
          <div className="org-stat">
            <span className="org-stat-value">{site.summary.upcomingShifts}</span>
            <span className="org-stat-label">Still to come</span>
          </div>
          {/* No time clock: the punch counts are not measured, never 0. */}
          <div className="org-stat">
            <span className="org-stat-value">{site.summary.punchesInside ?? 'Not measured'}</span>
            <span className="org-stat-label">Clock-ins inside the fence</span>
          </div>
          <div className="org-stat">
            <span className="org-stat-value">{site.summary.punchesOutside ?? 'Not measured'}</span>
            <span className="org-stat-label">Clock-ins outside</span>
          </div>
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-12)', fontSize: 13 }}>
          Times at this site are read in {site.effectiveTimezone}
          {site.timezone ? ' (set on the site).' : ' — your organization’s timezone.'}
        </p>
      </SettingsCard>

      <SiteLocationCard site={site} canManage={editable} onChanged={invalidate} />
      <OperatingHoursCard site={site} canManage={editable} onChanged={invalidate} />
      <SitePrograms site={site} canManage={editable} onChanged={invalidate} />
      <SiteLifecycle
        site={site}
        canManage={canManage}
        onChanged={invalidate}
        onDeleted={() => navigate('/settings/sites')}
      />
    </div>
  );
}

// --- Address, position, geofence --------------------------------------------

function SiteLocationCard({
  site,
  canManage,
  onChanged,
}: {
  site: SitesNS.SiteDetail;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [form, setForm] = useState({
    name: site.name,
    addressLine1: site.addressLine1 ?? '',
    addressLine2: site.addressLine2 ?? '',
    city: site.city ?? '',
    region: site.region ?? '',
    postalCode: site.postalCode ?? '',
    country: site.country ?? '',
    timezone: site.timezone ?? '',
  });
  const [coords, setCoords] = useState<{
    latitude: number | null;
    longitude: number | null;
    source: string;
    resolvedAddress: string | null;
    /** Whether the position was touched in this editing session. */
    touched: boolean;
  }>({
    latitude: site.latitude,
    longitude: site.longitude,
    source: site.coordinateSource,
    resolvedAddress: site.geocodedAddress,
    touched: false,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm({
      name: site.name,
      addressLine1: site.addressLine1 ?? '',
      addressLine2: site.addressLine2 ?? '',
      city: site.city ?? '',
      region: site.region ?? '',
      postalCode: site.postalCode ?? '',
      country: site.country ?? '',
      timezone: site.timezone ?? '',
    });
    setCoords({
      latitude: site.latitude,
      longitude: site.longitude,
      source: site.coordinateSource,
      resolvedAddress: site.geocodedAddress,
      touched: false,
    });
  }, [site]);

  const save = useMutation({
    mutationFn: () =>
      sites.update(site.id, {
        name: form.name.trim(),
        addressLine1: form.addressLine1.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        city: form.city.trim() || null,
        region: form.region.trim() || null,
        postalCode: form.postalCode.trim() || null,
        country: form.country.trim() || null,
        timezone: form.timezone.trim() || null,
        // Only send coordinates when they were actually touched — otherwise an
        // untouched save would rewrite the provenance of a position nobody moved.
        ...(coords.touched
          ? {
              coordinates:
                coords.latitude != null && coords.longitude != null
                  ? {
                      latitude: coords.latitude,
                      longitude: coords.longitude,
                      source:
                        coords.source === 'geocoded' ? ('geocoded' as const) : ('manual' as const),
                      resolvedAddress: coords.resolvedAddress,
                    }
                  : null,
            }
          : {}),
      }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'We couldn’t save that.'),
  });

  const dirty =
    coords.touched ||
    form.name !== site.name ||
    form.addressLine1 !== (site.addressLine1 ?? '') ||
    form.addressLine2 !== (site.addressLine2 ?? '') ||
    form.city !== (site.city ?? '') ||
    form.region !== (site.region ?? '') ||
    form.postalCode !== (site.postalCode ?? '') ||
    form.country !== (site.country ?? '') ||
    form.timezone !== (site.timezone ?? '');

  return (
    <>
      <SettingsCard>
        <h2 className="rn-card-title">Address &amp; position</h2>
        {!canEdit(site.status) ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            This site is retired, so it is read-only. Reactivate it below to make changes.
          </p>
        ) : null}
        <div className="set-fields" style={{ marginTop: 'var(--space-14)' }}>
          <SettingsField label="Name">
            <input
              style={{ marginBottom: 0 }}
              value={form.name}
              disabled={!canManage}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Street">
            <input
              style={{ marginBottom: 0 }}
              value={form.addressLine1}
              disabled={!canManage}
              onChange={(e) => setForm({ ...form, addressLine1: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Suite / floor">
            <input
              style={{ marginBottom: 0 }}
              value={form.addressLine2}
              disabled={!canManage}
              onChange={(e) => setForm({ ...form, addressLine2: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="City">
            <input
              style={{ marginBottom: 0 }}
              value={form.city}
              disabled={!canManage}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="State / region">
            <input
              style={{ marginBottom: 0 }}
              value={form.region}
              disabled={!canManage}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Postal code">
            <input
              style={{ marginBottom: 0 }}
              value={form.postalCode}
              disabled={!canManage}
              onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
            />
          </SettingsField>
          <SettingsField label="Country">
            <input
              style={{ marginBottom: 0 }}
              value={form.country}
              disabled={!canManage}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
            />
          </SettingsField>
          <SettingsField
            label="Timezone"
            hint={`Leave blank to follow your organization (${site.effectiveTimezone}).`}
          >
            <input
              style={{ marginBottom: 0 }}
              placeholder="e.g. America/Chicago"
              value={form.timezone}
              disabled={!canManage}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            />
          </SettingsField>
        </div>

        <div style={{ marginTop: 'var(--space-16)' }}>
          <SiteMapPicker
            address={form}
            latitude={coords.latitude}
            longitude={coords.longitude}
            coordinateSource={coords.source}
            coordinateSetAt={site.coordinateSetAt}
            geofenceRadiusMeters={
              site.geofence && site.geofence.status === 'active' ? site.geofence.radiusMeters : null
            }
            disabled={!canManage}
            mapsApiKey={MAPS_KEY}
            onChange={(next) =>
              setCoords({
                latitude: next.latitude,
                longitude: next.longitude,
                source: next.source,
                resolvedAddress: next.resolvedAddress ?? null,
                touched: true,
              })
            }
            onClear={() =>
              setCoords({
                latitude: null,
                longitude: null,
                source: 'unknown',
                resolvedAddress: null,
                touched: true,
              })
            }
          />
        </div>

        {canManage ? (
          <SettingsActions
            dirty={dirty}
            valid={form.name.trim().length > 0}
            saving={save.isPending}
            saved={!dirty && save.isSuccess}
            error={error}
            onSave={() => save.mutate()}
            onCancel={() => {
              setForm({
                name: site.name,
                addressLine1: site.addressLine1 ?? '',
                addressLine2: site.addressLine2 ?? '',
                city: site.city ?? '',
                region: site.region ?? '',
                postalCode: site.postalCode ?? '',
                country: site.country ?? '',
                timezone: site.timezone ?? '',
              });
              setCoords({
                latitude: site.latitude,
                longitude: site.longitude,
                source: site.coordinateSource,
                resolvedAddress: site.geocodedAddress,
                touched: false,
              });
              setError(null);
            }}
          />
        ) : null}
      </SettingsCard>

      <GeofenceCard site={site} canManage={canManage} onChanged={onChanged} />
    </>
  );
}

function GeofenceCard({
  site,
  canManage,
  onChanged,
}: {
  site: SitesNS.SiteDetail;
  canManage: boolean;
  onChanged: () => void;
}) {
  const active = site.geofence != null && site.geofence.status === 'active';
  const [enabled, setEnabled] = useState(active);
  const [radius, setRadius] = useState(String(site.geofence?.radiusMeters ?? 150));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(site.geofence != null && site.geofence.status === 'active');
    setRadius(String(site.geofence?.radiusMeters ?? 150));
  }, [site.geofence]);

  const save = useMutation({
    mutationFn: () =>
      sites.setGeofence(site.id, {
        enabled,
        radiusMeters: enabled ? Number(radius) : undefined,
      }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t save the geofence.'),
  });

  const radiusNumber = Number(radius);
  const radiusValid =
    !enabled || (Number.isFinite(radiusNumber) && radiusNumber >= 1 && radiusNumber <= 50_000);
  const dirty = enabled !== active || (enabled && radiusNumber !== site.geofence?.radiusMeters);
  const positioned = site.latitude != null && site.longitude != null;

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Geofence</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        When this is on, clock-ins at this site are checked against the circle and recorded as
        inside or outside. What happens next — nothing, a warning, or an attestation — is set by
        your location policy under Location &amp; geofences.
      </p>
      {!positioned ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-10)' }}>
          This site has no position yet, so it can’t have a geofence. Find its address on the map
          above first.
        </p>
      ) : null}

      <label className="org-check" style={{ marginTop: 'var(--space-12)' }}>
        <input
          type="checkbox"
          checked={enabled}
          disabled={!canManage || !positioned}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>Check clock-ins against this site</span>
      </label>

      {enabled ? (
        <SettingsField
          label="Radius (metres)"
          hint="Between 1 m and 50 km."
          error={radiusValid ? undefined : 'The radius must be between 1 metre and 50 km.'}
        >
          <input
            style={{ marginBottom: 0, maxWidth: 180 }}
            inputMode="numeric"
            value={radius}
            disabled={!canManage}
            onChange={(e) => setRadius(e.target.value)}
          />
        </SettingsField>
      ) : site.geofence ? (
        <p className="rn-muted" style={{ marginTop: 'var(--space-10)', fontSize: 13 }}>
          Switching this off leaves the geofence in place but inactive, so clock-ins already
          evaluated against it keep their record.
        </p>
      ) : null}

      {canManage ? (
        <SettingsActions
          dirty={dirty}
          valid={radiusValid}
          saving={save.isPending}
          saved={!dirty && save.isSuccess}
          error={error}
          onSave={() => save.mutate()}
          onCancel={() => {
            setEnabled(active);
            setRadius(String(site.geofence?.radiusMeters ?? 150));
            setError(null);
          }}
          saveLabel="Save geofence"
        />
      ) : null}
    </SettingsCard>
  );
}

// --- Programs ---------------------------------------------------------------

function SitePrograms({
  site,
  canManage,
  onChanged,
}: {
  site: SitesNS.SiteDetail;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [selected, setSelected] = useState<string[]>(site.programs.map((p) => p.programId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(site.programs.map((p) => p.programId));
  }, [site.programs]);

  const options = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ limit: 200, sort: 'name' }),
  });

  const save = useMutation({
    mutationFn: () => sites.setPrograms(site.id, { programIds: selected }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t save those programs.'),
  });

  const currentIds = site.programs
    .map((p) => p.programId)
    .sort()
    .join(',');
  const dirty = [...selected].sort().join(',') !== currentIds;

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Programs operating here</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        The same relationship the program’s own screen edits — change it in either place and both
        stay in step.
      </p>
      {options.isLoading ? (
        <SkeletonSet surface="table" rows={3} />
      ) : options.isError && options.data === undefined ? (
        <LoadFailure
          error={options.error}
          message="We couldn’t load your programs just now. This is not the same as having none."
          onRetry={() => void options.refetch()}
        />
      ) : (options.data?.programs.length ?? 0) === 0 ? (
        <SuccessEmpty message="No programs yet. Create one under Programs and it will appear here." />
      ) : (
        <div className="org-checklist" style={{ marginTop: 'var(--space-12)' }}>
          {(options.data?.programs ?? []).map((program) => (
            <label key={program.id} className="org-check">
              <input
                type="checkbox"
                disabled={!canManage}
                checked={selected.includes(program.id)}
                onChange={(e) =>
                  setSelected(
                    e.target.checked
                      ? [...selected, program.id]
                      : selected.filter((id) => id !== program.id),
                  )
                }
              />
              <span>{program.name}</span>
              {site.programs.find((p) => p.programId === program.id)?.isPrimary ? (
                <span className="rn-muted"> · this is their main site</span>
              ) : null}
            </label>
          ))}
        </div>
      )}
      {canManage ? (
        <SettingsActions
          dirty={dirty}
          valid
          saving={save.isPending}
          saved={!dirty && save.isSuccess}
          error={error}
          onSave={() => save.mutate()}
          onCancel={() => {
            setSelected(site.programs.map((p) => p.programId));
            setError(null);
          }}
          saveLabel="Save programs"
        />
      ) : null}
    </SettingsCard>
  );
}

// --- Lifecycle --------------------------------------------------------------

function SiteLifecycle({
  site,
  canManage,
  onChanged,
  onDeleted,
}: {
  site: SitesNS.SiteDetail;
  canManage: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  // PH-UX-004. `confirm` above gates RETIRE, which is reversible. The hard
  // DELETE lives in a sibling JSX branch that this state never reaches, so the
  // file's confirm tokens said "confirmed" while the irreversible control was
  // the bare one. Separate state, deliberately named, so the two cannot be
  // mistaken for each other again.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retire = useMutation({
    mutationFn: () => sites.retire(site.id),
    onSuccess: () => {
      setConfirm(false);
      onChanged();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'We couldn’t retire it.'),
  });
  const reactivate = useMutation({
    mutationFn: () => sites.reactivate(site.id),
    onSuccess: onChanged,
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t reactivate it.'),
  });
  const destroy = useMutation({
    mutationFn: () => sites.remove(site.id),
    onSuccess: onDeleted,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'We couldn’t delete it.'),
  });

  if (!canManage) return null;

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Lifecycle</h2>
      {error ? (
        <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
          {error}
        </p>
      ) : null}

      {site.status === 'retired' ? (
        <>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            This site is retired. Its history — clock events, past shifts, geofence evaluations — is
            untouched and still reportable. Reactivating makes it available for new work again; its
            geofence stays switched off until you turn it back on deliberately.
          </p>
          <button
            type="button"
            className="signin-devbtn"
            style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
            disabled={reactivate.isPending}
            onClick={() => reactivate.mutate()}
          >
            {reactivate.isPending ? 'Reactivating…' : 'Reactivate site'}
          </button>
        </>
      ) : (
        <>
          <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
            Retiring takes a site out of service without changing a single historical record. It
            stops being offered for new schedules, and its geofence is switched off so it no longer
            takes part in clock-in checks.
          </p>
          {site.upcomingShiftCount > 0 ? (
            <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }}>
              {site.upcomingShiftCount} {site.upcomingShiftCount === 1 ? 'shift is' : 'shifts are'}{' '}
              already scheduled here in the future. Retiring won’t cancel{' '}
              {site.upcomingShiftCount === 1 ? 'it' : 'them'} — you’ll be told about{' '}
              {site.upcomingShiftCount === 1 ? 'it' : 'them'} under Needs attention until{' '}
              {site.upcomingShiftCount === 1 ? 'it is' : 'they are'} moved.
            </p>
          ) : null}
          {confirm ? (
            <div className="set-actions">
              <button
                type="button"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={retire.isPending}
                onClick={() => retire.mutate()}
              >
                {retire.isPending ? 'Retiring…' : 'Yes, retire this site'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={() => setConfirm(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className="rn-btn-quiet" onClick={() => setConfirm(true)}>
              Retire site
            </button>
          )}
        </>
      )}

      {site.deletable ? (
        <>
          <p className="rn-muted" style={{ marginTop: 'var(--space-16)', fontSize: 13 }}>
            Nothing references this site yet, so it can still be deleted outright.{' '}
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={destroy.isPending}
              onClick={() => setConfirmDelete(true)}
            >
              Delete permanently
            </button>
          </p>
          {/* Outside the <p>: the dialog renders a div, which is invalid
              inside a paragraph and would be re-parented by the browser. */}
          <ConfirmAction
            open={confirmDelete}
            title={`Delete ${site.name} permanently?`}
            consequence={
              `This deletes the site “${site.name}” outright — the row itself, not just its ` +
              `status. There is no undo and no retired copy to restore from; if you need it ` +
              `back you will have to create it again from scratch, including its address and ` +
              `geofence. If you only want it out of service, cancel and use Retire instead.`
            }
            confirmLabel="Delete permanently"
            pendingLabel="Deleting…"
            pending={destroy.isPending}
            onCancel={() => setConfirmDelete(false)}
            onConfirm={() => destroy.mutate()}
          />
        </>
      ) : (
        <p className="rn-muted" style={{ marginTop: 'var(--space-16)', fontSize: 13 }}>
          Schedules, geofences or programs already refer to this site, so it can only be retired —
          deleting it would strand those records.
        </p>
      )}
    </SettingsCard>
  );
}

// --- Operating hours ---------------------------------------------------------

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type DayKey = (typeof DAY_KEYS)[number];
const DAY_LABELS: Record<DayKey, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};
type DayState = { open: string; close: string };
type HoursState = Record<DayKey, DayState>;

function emptyHours(): HoursState {
  return DAY_KEYS.reduce((acc, d) => {
    acc[d] = { open: '', close: '' };
    return acc;
  }, {} as HoursState);
}

function hoursFrom(value: SitesNS.OperatingHours | null): HoursState {
  const state = emptyHours();
  if (!value) return state;
  for (const d of DAY_KEYS) {
    const day = value[d];
    if (day) state[d] = { open: day.open ?? '', close: day.close ?? '' };
  }
  return state;
}

/**
 * A day with neither time set is CLOSED, not "unknown" — which is why a fully
 * blank week is sent as `null` (no hours recorded) rather than as seven closed
 * days. The two mean different things and an operator can express both.
 */
function hoursTo(state: HoursState): SitesNS.OperatingHours | null {
  const out: SitesNS.OperatingHours = {};
  let any = false;
  for (const d of DAY_KEYS) {
    const { open, close } = state[d];
    if (open || close) {
      out[d] = { open: open || null, close: close || null };
      any = true;
    } else {
      out[d] = null;
    }
  }
  return any ? out : null;
}

function OperatingHoursCard({
  site,
  canManage,
  onChanged,
}: {
  site: SitesNS.SiteDetail;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [hours, setHours] = useState<HoursState>(() => hoursFrom(site.operatingHours));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHours(hoursFrom(site.operatingHours));
  }, [site.operatingHours]);

  const save = useMutation({
    mutationFn: () => sites.update(site.id, { operatingHours: hoursTo(hours) }),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (e: unknown) =>
      setError(e instanceof Error ? e.message : 'We couldn’t save those hours.'),
  });

  const dirty = JSON.stringify(hoursTo(hours)) !== JSON.stringify(site.operatingHours ?? null);

  return (
    <SettingsCard>
      <h2 className="rn-card-title">Opening hours</h2>
      <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
        When this site is open, in {site.effectiveTimezone}. These are the building’s hours, not
        anyone’s shift pattern — staff schedules are set under Scheduling. Recorded for reference:
        Phibian does not yet schedule or raise alerts from them. Leave a day blank to mark it
        closed.
      </p>
      <div className="org-hours" style={{ marginTop: 'var(--space-12)' }}>
        {DAY_KEYS.map((d) => (
          <div key={d} className="org-hours-row">
            <span id={`site-hours-${d}`}>{DAY_LABELS[d]}</span>
            <input
              style={{ marginBottom: 0 }}
              type="time"
              aria-label={`${DAY_LABELS[d]} opening time`}
              disabled={!canManage}
              value={hours[d].open}
              onChange={(e) => setHours((h) => ({ ...h, [d]: { ...h[d], open: e.target.value } }))}
            />
            <input
              style={{ marginBottom: 0 }}
              type="time"
              aria-label={`${DAY_LABELS[d]} closing time`}
              disabled={!canManage}
              value={hours[d].close}
              onChange={(e) => setHours((h) => ({ ...h, [d]: { ...h[d], close: e.target.value } }))}
            />
            <span className="rn-muted">{hours[d].open || hours[d].close ? '' : 'Closed'}</span>
          </div>
        ))}
      </div>
      {canManage ? (
        <SettingsActions
          dirty={dirty}
          valid
          saving={save.isPending}
          saved={!dirty && save.isSuccess}
          error={error}
          onSave={() => save.mutate()}
          onCancel={() => {
            setHours(hoursFrom(site.operatingHours));
            setError(null);
          }}
          saveLabel="Save hours"
        />
      ) : null}
    </SettingsCard>
  );
}
